import type {
  AnalyticsComparison,
  AnalyticsFilterOption,
  AnalyticsPeriod,
  AnalyticsTrendGrain,
  LensValueFormat,
} from '~/types/leLens';

export const GROWTH_PAGES = ['overview', 'acquisition', 'lifecycle', 'gha'] as const;
export type GrowthPageId = (typeof GROWTH_PAGES)[number];

export const GROWTH_SOURCES = [
  'paid_media',
  'gha',
  'promotions',
  'sessions',
  'offer_funnel',
  'checkout',
  'email',
] as const;
export type GrowthSourceId = (typeof GROWTH_SOURCES)[number];

export const GROWTH_ATTRIBUTIONS = ['operating', 'last_click_session', 'first_click_365d', 'shapley_90d'] as const;
export type GrowthAttributionId = (typeof GROWTH_ATTRIBUTIONS)[number];

export type GrowthMetric = {
  id: string;
  label: string;
  definition: string;
  status: 'ready' | 'unavailable';
  format: LensValueFormat;
  favourableDirection: 'up' | 'down' | 'contextual';
  value?: number;
  comparisonValue?: number;
  absoluteChange?: number;
  percentChange?: number | null;
  unavailableReason?: string;
};

export type GrowthTrendPoint = {
  bucket: string;
  value: number | null;
  comparisonValue?: number | null;
  partial?: boolean;
};

export type GrowthBreakdownRow = {
  id: string;
  label: string;
  value: number;
  comparisonValue?: number | null;
  absoluteChange?: number | null;
  percentChange?: number | null;
  share?: number | null;
  metrics?: Record<string, number | null>;
};

export type GrowthFunnelStep = {
  id: string;
  label: string;
  value: number;
  rateFromPrevious?: number | null;
};

export type GrowthSourceCapability = {
  id: GrowthSourceId;
  label: string;
  available: boolean;
  reason?: string;
  pages: Array<GrowthPageId>;
  datasetIds: Array<string>;
  attributionLocked: boolean;
  metricIds: Array<string>;
  breakdowns: Array<{ id: string; label: string }>;
  filters: Array<{ id: keyof GrowthViewState['filters']; label: string }>;
};

export type GrowthSnapshot = {
  domain: {
    id: 'growth';
    version: string;
    name: string;
    description: string;
    datasetIds: Array<string>;
  };
  page: {
    id: GrowthPageId;
    label: string;
    description: string;
  };
  source: {
    id: GrowthSourceId;
    label: string;
    catalogueIds: Array<string>;
    datasetNames: Array<string>;
  };
  attribution: {
    id: GrowthAttributionId;
    label: string;
    locked: boolean;
    note: string;
  };
  period: {
    id: AnalyticsPeriod;
    label: string;
    from: string;
    to: string;
    comparison: null | {
      mode: Exclude<AnalyticsComparison, 'none'>;
      label: string;
      from: string;
      to: string;
    };
    timezone: 'Australia/Sydney';
  };
  filters: {
    selected: Partial<GrowthViewState['filters']>;
    options: {
      sources: Array<GrowthSourceCapability>;
      attributions: Array<{
        id: GrowthAttributionId;
        label: string;
        locked: boolean;
        note: string;
      }>;
      breakdowns: Array<{ id: string; label: string }>;
      channel: Array<AnalyticsFilterOption>;
      campaign: Array<AnalyticsFilterOption>;
      segment?: Array<AnalyticsFilterOption>;
      market: Array<AnalyticsFilterOption>;
      platform: Array<AnalyticsFilterOption>;
      promoCode: Array<AnalyticsFilterOption>;
      promoType: Array<AnalyticsFilterOption>;
      voucher: Array<AnalyticsFilterOption>;
      brand: Array<AnalyticsFilterOption>;
      product: Array<AnalyticsFilterOption>;
    };
  };
  metrics: Array<GrowthMetric>;
  trend: {
    metricId: string;
    grain: AnalyticsTrendGrain;
    points: Array<GrowthTrendPoint>;
    series?: Record<string, Array<GrowthTrendPoint>>;
  };
  breakdowns: Array<{
    id: string;
    label: string;
    rows: Array<GrowthBreakdownRow>;
    dimension: string;
    metricId: string;
    trend?: {
      grain: AnalyticsTrendGrain;
      series: Array<{
        id: string;
        label: string;
        points: Array<GrowthTrendPoint>;
      }>;
    };
  }>;
  funnel?: { steps: Array<GrowthFunnelStep> };
  sourceCapabilities: Array<GrowthSourceCapability>;
  caveats: Array<{ id: string; title: string; detail: string; severity: 'info' | 'warning' }>;
  refresh: {
    cadence: string;
    queriedAt: string;
    lastRefreshedAt: string | null;
    completedThrough: string;
    dataTo: string;
    state: 'fresh' | 'late' | 'failed' | 'observed' | 'not_observed';
    note: string;
  };
};

export type GrowthViewState = {
  page: GrowthPageId;
  source: GrowthSourceId;
  attribution: GrowthAttributionId;
  period: AnalyticsPeriod;
  from?: string;
  to?: string;
  comparison: AnalyticsComparison;
  comparisonFrom?: string;
  comparisonTo?: string;
  trendGrain?: AnalyticsTrendGrain;
  focusMetric?: string;
  breakdown?: string;
  /** Explore (controls, Investigate by, chart, table, funnel) is open. The page loads with it closed. */
  detailOpen?: boolean;
  filters: {
    market: Array<string>;
    channel: Array<string>;
    campaign: Array<string>;
    segment: Array<string>;
    platform: Array<string>;
    promoCode: Array<string>;
    promoType: Array<string>;
    voucher: Array<string>;
    brand: Array<string>;
    product: Array<string>;
  };
};
